import { filterKeyToAuditPropertyMapping } from './policyKeyMappings';

export type FilterKey = keyof typeof filterKeyToAuditPropertyMapping;
export type FilterPath = (typeof filterKeyToAuditPropertyMapping)[FilterKey][number];

export const mandatoryKeys = ['id', 'TenantId'];
/**
 * Filters an object by whitelisting selector paths.
 * Simple keys are mapped to selector paths
 *
 * Selector semantics:
 *   - Dot notation for nesting: "user.address.city"
 *   - Arrays are handled implicitly: "orders.id" and
 *     will map the remaining path over each array element.
 */
export class AuditObjectFilter {
    private readonly compiledPaths: string[][];

    constructor(simpleFilterKeys: string[] = []) {
        const paths = this.mapKeysToPaths(simpleFilterKeys);
        this.compiledPaths = paths.map((p) => p.split('.'));
    }

    /**
     * Returns a new object containing only allowed properties.
     *
     * Missing paths are ignored.
     */
    filter<T extends object>(source: T): Record<string, unknown> {
        const result: Record<string, unknown> = {};

        for (const segments of this.compiledPaths) {
            this.apply(source, result, segments);
        }

        return result as Partial<T>;
    }

    toJSON<T extends object>(source: T, space: number | string = 0) {
        return JSON.stringify(this.filter(source), null, space);
    }

    /**
     * Expands simple filter keys into their mapped selector paths.
     *
     * Some Paths are always returned.
     * Each key may map to 0, 1 or many concrete object paths via 'filterKeyMapping'
     * Unknown keys are ignored,
     * Duplicate selector paths are removed.
     */
    private mapKeysToPaths(keys: string[]): string[] {
        const seen: Record<string, true> = Object.create(null);
        const out: string[] = [];

        const allKeys = Array.from(new Set<FilterKey>([...mandatoryKeys, ...keys]));

        for (const k of allKeys) {
            const paths = filterKeyToAuditPropertyMapping[k];
            if (!paths) continue;

            for (const p of paths) {
                if (!seen[p]) {
                    seen[p] = true;
                    out.push(p);
                }
            }
        }

        return out;
    }

    /**
     * Recursively applies a selector path to the source value.
     *
     * Behaviour:
     *   If current value is an array, the same path is applied to each element.
     *   If a key does not exist, traversal stops silently.
     *   Leaf values are copied directly.
     *   Empty objects/array entries are pruned.
     */
    private apply(object: unknown, filteredObject: Record<string, unknown>, segments: string[]): void {
        if (object == null || typeof object !== 'object') return;

        const [key, ...rest] = segments;

        // If the path specified doesnt exist in our source object then nothing to copy over
        if (!(key in object)) return;

        const value = (object as Record<string, unknown>)[key];

        // if we have no child property specified in our both copy the whole source value across
        if (rest.length === 0) {
            filteredObject[key] = value;
            return;
        }

        // since we have a child property specified
        // if the value is an array apply the child selectors to each value in the array
        if (Array.isArray(value)) {
            if (value.length == 0) {
                // the array has no children
                return;
            }

            let filteredValue: unknown[];
            const existingFilteredValue = filteredObject[key];
            if (Array.isArray(existingFilteredValue)) {
                filteredValue = existingFilteredValue as unknown[];
            } else {
                filteredValue = [];
                filteredObject[key] = filteredValue;
            }

            for (let i = 0; i < value.length; i++) {
                let child;
                if (!filteredValue[i]) {
                    child = {};
                    filteredValue[i] = child;
                } else {
                    child = filteredValue[i] as Record<string, unknown>;
                }

                this.apply(value[i], child, rest);
            }
        } else {
            // if the value is an 'object' then see if it has a child key
            let child;
            if (!filteredObject[key]) {
                child = {};
                filteredObject[key] = child;
            } else {
                child = filteredObject[key] as Record<string, unknown>;
            }

            this.apply(value, child, rest);
        }
    }
}
